/** Form + table helpers for MCC Student page */

export const INPUT_CLASS =
  "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 text-sm text-gray-800";

export const LABEL_CLASS = "text-xs font-semibold text-primary mb-1 block";

export const emptyCreateForm = () => ({
  registration_id: "",
  name: "",
  phone_number: "",
  date_of_birth: "",
  father_name: "",
  mother_name: "",
  course_affiliation_: "",
  district: "",
  city_of_ITI: "",
  itiname: "",
  tradename: "",
  industryname: "",
  batch_of_trade: "",
  gender: "",
});

/** Edit modal: same shape + server id */
export const emptyEditForm = () => ({
  ...emptyCreateForm(),
  id: null,
  username: "",
  password: "",
});

/**
 * Map GET /mcc/students/:id response into edit form state.
 */
export const mapTraineeDetailToForm = (d) => ({
  id: d.id,
  registration_id: d.registration_id ?? "",
  username: d.username ?? "",
  password: d.password ?? "",
  name: d.name ?? "",
  phone_number: sanitizePhoneDigits(d.phone_number),
  date_of_birth: d.date_of_birth ? String(d.date_of_birth).slice(0, 10) : "",
  father_name: d.father_name ?? "",
  mother_name: d.mother_name ?? "",
  course_affiliation_: d.course_affiliation_ ?? "",
  district: d.district_of_ITI ?? "",
  city_of_ITI: d.city_of_ITI ?? "",
  itiname: d.iti_id != null && d.iti_id !== "" ? Number(d.iti_id) : "",
  tradename: d.trade_id != null && d.trade_id !== "" ? Number(d.trade_id) : "",
  industryname: d.industry_id != null && d.industry_id !== "" ? Number(d.industry_id) : "",
  batch_of_trade: d.batch_of_trade ?? "",
  gender: d.gender ?? "",
});

export const TABLE_COLUMNS = [
  { header: "REGISTRATION ID", accessor: "registration_id" },
  { header: "USERNAME", accessor: "username" },
  { header: "PASSWORD", accessor: "password" },
  { header: "NAME", accessor: "name" },
  { header: "PHONE", accessor: "phone" },
  { header: "DISTRICT", accessor: "district" },
  { header: "CITY", accessor: "city" },
  { header: "ITI", accessor: "iti" },
  { header: "BATCH OF TRADE", accessor: "batch_of_trade" },
  { header: "TRADE", accessor: "trade" },
  {
    header: "INDUSTRY",
    accessor: "industry",
    className: "min-w-[16rem] w-[16rem]",
    cellClassName: "align-top",
    render: (row) => {
      const value = row?.industry ? String(row.industry) : "-";
      return (
        <div className="max-w-[16rem] truncate" title={value}>
          {value}
        </div>
      );
    },
  },
];

/** Digits only, max 10 characters (for controlled input). */
export const sanitizePhoneDigits = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);

/** Non-empty string of digits, length 1–10 (integer-style phone). */
export const isValidPhoneDigits = (value) => /^\d{1,10}$/.test(String(value ?? "").trim());

/** Letters and spaces only — blocks digits and symbols while typing (MCC name field). */
export const sanitizeNameLettersOnly = (value) =>
  String(value ?? "").replace(/[^a-zA-Z\s]/g, "");

/** Trimmed name: one or more words, each word letters only. */
const isValidNameLettersOnly = (trimmed) =>
  /^[a-zA-Z]+(?:\s+[a-zA-Z]+)*$/.test(trimmed);

/**
 * Client-side validation before POST /v1/api/mcc/students.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export const validateCreateStudentForm = (form) => {
  if (!form.registration_id?.trim()) {
    return { ok: false, message: "Registration ID is required" };
  }
  const nameTrim = form.name?.trim() ?? "";
  if (!nameTrim) {
    return { ok: false, message: "Name is required" };
  }
  if (!isValidNameLettersOnly(nameTrim)) {
    return {
      ok: false,
      message: "Name must use letters only (spaces between words are allowed)",
    };
  }
  const phone = String(form.phone_number ?? "").trim();
  if (!phone) {
    return { ok: false, message: "Phone number is required" };
  }
  if (!isValidPhoneDigits(phone)) {
    return {
      ok: false,
      message: "Phone must be digits only, 1 to 10 digits",
    };
  }
  if (!form.date_of_birth) {
    return { ok: false, message: "Date of Birth is required" };
  }
  if (!form.father_name?.trim()) {
    return { ok: false, message: "Father's Name is required" };
  }
  if (!form.mother_name?.trim()) {
    return { ok: false, message: "Mother's Name is required" };
  }
  if (!form.course_affiliation_) {
    return { ok: false, message: "Course Affiliation is required" };
  }
  if (!form.district || !form.city_of_ITI?.trim()) {
    return { ok: false, message: "District and City of ITI are required" };
  }
  if (!form.itiname || !form.tradename || !form.industryname) {
    return {
      ok: false,
      message: "Please select District, ITI, Trade and Industry",
    };
  }
  if (!form.batch_of_trade) {
    return { ok: false, message: "Batch of Trade is required" };
  }
  if (!form.gender) {
    return { ok: false, message: "Gender is required" };
  }
  return { ok: true };
};

export const validateUpdateStudentForm = (form) => {
  const base = validateCreateStudentForm(form);
  if (!base.ok) return base;
  if (!String(form.password ?? "").trim()) {
    return { ok: false, message: "Password is required" };
  }
  return { ok: true };
};

/**
 * Build JSON body for create trainee (username/password generated server-side).
 */
export const buildCreateStudentPayload = (form) => ({
  registration_id: form.registration_id.trim(),
  name: form.name.trim(),
  phone_number: sanitizePhoneDigits(form.phone_number),
  date_of_birth: form.date_of_birth || undefined,
  father_name: form.father_name?.trim() || undefined,
  mother_name: form.mother_name?.trim() || undefined,
  course_affiliation_: form.course_affiliation_?.trim() || undefined,
  district_of_ITI: form.district || undefined,
  city_of_ITI: form.city_of_ITI?.trim() || undefined,
  iti_id: Number(form.itiname),
  trade_id: Number(form.tradename),
  industry_id: Number(form.industryname),
  batch_of_trade: form.batch_of_trade?.trim() || undefined,
  gender: form.gender || undefined,
});

export const buildUpdateStudentPayload = (form) => ({
  ...buildCreateStudentPayload(form),
  password: String(form.password ?? "").trim(),
});
